import { forwardRef, type ReactNode } from "react";

import { cn } from "@btnet/utilities-core";

import { BTBreadcrumb } from "commonComponents/btWrappers/BTBreadcrumb/BTBreadcrumb";
import { BTItemHeader } from "commonComponents/btWrappers/BTItemHeader/BTItemHeader";
import { type IBTItemHeaderActionButtonsProps } from "commonComponents/btWrappers/BTItemHeader/BTItemHeader.types";
import { Frame } from "commonComponents/btWrappers/Frame/Frame";

interface IDetailFrameProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
    /**
     * Content rendered inside the fixed action-bar header. Typically a
     * `DetailFrame.ActionButtons`.
     */
    actionBar: ReactNode;
    /** Body content (page body). Breadcrumbs and title are rendered via `DetailFrame.Title`. */
    children: ReactNode;
    /**
     * Additional pixel offset to subtract from the root's viewport height.
     * Forwarded to the underlying `Frame`.
     * @default 0
     */
    availableHeightOffset?: number;
    /**
     * Optional full-width banner rendered above the fixed action-bar header and
     * outside its padding. Use for page-level informational alerts (e.g. autosave
     * notifications) that should span edge-to-edge.
     */
    bannerSlot?: ReactNode;
    /**
     * Optional ref to the fixed action-bar header element. Forwarded to the
     * underlying `Frame.FixedHeader` so consumers can measure header height —
     * e.g. to offset an overlay comment drawer below the action bar.
     */
    headerRef?: React.Ref<HTMLElement>;
}

type IDetailFrameTitleProps = {
    /** Test ID forwarded to the inner `BTItemHeader`. */
    testid: string;
    /** Aria-label for the `BTItemHeader` landmark region. */
    ariaLabel: string;
    /** Additive className on the inner `BTItemHeader`. */
    className?: string;
    /** Title content — typically a `BTItemHeader.TextArea` for dynamic title content. */
    children: ReactNode;
} & (
    | {
          /**
           * Breadcrumb items rendered inside a `BTBreadcrumb`. Typically a
           * fragment of `BTBreadcrumb.Item` children.
           */
          breadcrumbSlot: ReactNode;
          /** Test ID forwarded to the inner `BTBreadcrumb`. */
          breadcrumbTestid: string;
      }
    | { breadcrumbSlot?: undefined; breadcrumbTestid?: undefined }
);

const DetailFrameTitle = (props: IDetailFrameTitleProps) => {
    const { testid, ariaLabel, className, children } = props;
    return (
        <BTItemHeader testid={testid} ariaLabel={ariaLabel} className={cn("pb-6", className)}>
            {props.breadcrumbSlot !== undefined && (
                <div className="flex flex-row bt-breadcrumb-container">
                    <BTBreadcrumb testid={props.breadcrumbTestid} className="pt-1 pb-2">
                        {props.breadcrumbSlot}
                    </BTBreadcrumb>
                </div>
            )}
            <div className="flex flex-wrap items-start w-full gap-2">{children}</div>
        </BTItemHeader>
    );
};
DetailFrameTitle.displayName = "DetailFrame.Title";

const DetailFrameActionButtons = ({ className, ...props }: IBTItemHeaderActionButtonsProps) => (
    <BTItemHeader.ActionButtons {...props} className={cn("py-0!", className)} />
);
DetailFrameActionButtons.displayName = "DetailFrame.ActionButtons";

/**
 * The canonical padding/spacing for a detail-page fixed header. Single source of truth so the
 * built-in `DetailFrame` shell and any standalone consumer stay in sync.
 */
const DETAIL_FRAME_FIXED_HEADER_CLASSES = "px-4! md:px-8! py-3! lg:py-4! row-gap-2";

interface IDetailFrameFixedActionBarProps extends React.HTMLAttributes<HTMLElement> {
    children: ReactNode;
}

const DetailFrameFixedActionBar = ({
    children,
    className,
    ...otherProps
}: IDetailFrameFixedActionBarProps) => (
    <Frame.FixedHeader className={cn(DETAIL_FRAME_FIXED_HEADER_CLASSES, className)} {...otherProps}>
        {children}
    </Frame.FixedHeader>
);
DetailFrameFixedActionBar.displayName = "DetailFrame.FixedActionBar";

interface IDetailFrameContentProps extends React.HTMLAttributes<HTMLDivElement> {
    children: ReactNode;
}

const DetailFrameContent = forwardRef<HTMLDivElement, IDetailFrameContentProps>(
    ({ children, className, ...otherProps }, ref) => (
        <div ref={ref} className={cn("flex flex-col", className)} {...otherProps}>
            {children}
        </div>
    ),
);
DetailFrameContent.displayName = "DetailFrame.Content";

const DetailFrameComponent = forwardRef<HTMLDivElement, IDetailFrameProps>(
    (
        {
            actionBar,
            children,
            className,
            availableHeightOffset,
            bannerSlot,
            headerRef,
            ...otherProps
        },
        ref,
    ) => (
        <Frame
            ref={ref}
            className={cn("bds-w-full", className)}
            availableHeightOffset={availableHeightOffset}
            {...otherProps}
        >
            {bannerSlot}
            <Frame.FixedHeader ref={headerRef} className={DETAIL_FRAME_FIXED_HEADER_CLASSES}>
                {actionBar}
            </Frame.FixedHeader>
            <Frame.Content
                data-testid="detail-frame-content"
                className="pt-0! px-4! md:px-8! scrollbar-gutter-stable-both"
            >
                <div
                    data-testid="detail-frame-body"
                    className="flex flex-col max-w-screen-xl mx-auto w-full"
                >
                    {children}
                </div>
            </Frame.Content>
        </Frame>
    ),
);
DetailFrameComponent.displayName = "DetailFrame";

/**
 * Detail-page shell. Composes `Frame` + `Frame.FixedHeader` + `Frame.Content`
 * with the canonical Tailwind classes for an entity detail page:
 *
 * - fixed action bar (with auto drop-shadow on scroll)
 * - scrollable body with stable scrollbar gutter on both edges
 * - body content centered with `max-w-screen-xl` and `mx-auto`
 *
 * Use this for any entity detail page (Daily Log, To-Do, Submittal, etc.).
 * If you need the bare layout primitives, drop down to `Frame` directly.
 *
 * Pair with `DetailFrame.Title` for the canonical breadcrumb + title block.
 */
export const DetailFrame = Object.assign(DetailFrameComponent, {
    /**
     * Breadcrumb + title block for `DetailFrame`. Renders a `BTItemHeader` with
     * the canonical detail-page spacing (`pb-6`) and, optionally, a
     * `BTBreadcrumb` row above the title.
     *
     * The breadcrumb is an all-or-nothing slot: pass both `breadcrumbSlot` and
     * `breadcrumbTestid` to render it, or pass neither to omit the row entirely.
     * Supplying only one is a compile error — the breadcrumb requires a stable
     * test id, and a bare test id with no breadcrumb content has nothing to label.
     *
     * `children` should be passed as siblings — typically a
     * `BTItemHeader.TextArea` (the title) plus any number of metadata siblings
     * such as a `BTStatusBadge` for status or `BdsText` for author/date attribution.
     * The first child gets the title-row slot (`w-full` on mobile, `auto` on
     * `sm+`) so the title takes the full row on narrow viewports and shares the
     * row with metadata on wider ones; remaining children flow alongside in a
     * `flex flex-wrap items-start` row.
     */
    Title: DetailFrameTitle,

    /**
     * Action-bar slot for `DetailFrame`. Thin wrapper over
     * `BTItemHeader.ActionButtons` that bakes in the zero-vertical-padding tweak
     * the detail-page fixed header expects (`py-0!`). All props forward
     * directly to the underlying component.
     */
    ActionButtons: DetailFrameActionButtons,

    /**
     * Standalone fixed-header wrapper carrying the canonical detail-page action-bar padding
     * and scroll drop-shadow. The `DetailFrame` shell uses this internally for its `actionBar`;
     * consumers that can't mount a full `DetailFrame` (e.g. a header slotted into a modal or a
     * BdsLayout page shell) can wrap their `DetailFrame.ActionButtons` (and `DetailFrame.Title`)
     * in this to inherit the exact same padding instead of hand-copying the classes.
     */
    FixedActionBar: DetailFrameFixedActionBar,

    /**
     * Body-content slot for `DetailFrame`. A flex column wrapper matching
     * `BdsCol`'s `display: flex; flex-direction: column` semantics, intended to
     * hold the scrollable body of a detail page underneath `DetailFrame.Title`.
     *
     * Native `<div>` rather than `BdsCol` so it inherits `HTMLAttributes` cleanly,
     * forwards refs, and keeps consumers on Tailwind utilities rather than BDS
     * layout classes for any additive styling.
     */
    Content: DetailFrameContent,
});
